// Secure Token Management Utility
import { jwtDecode } from 'jwt-decode';

function storageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function storageRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* private mode or blocked storage */
  }
}

class TokenManager {
  constructor() {
    this.tokenKey = 'realmJwt';
    this.userIdKey = 'realmUserId';
    this.emailKey = 'realmEmail';
    this.loginTimestampKey = 'loginTimestamp';
    this.currentUserKey = 'currentUser';
  }

  // Store token with expiration check
  setToken(token) {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      if (decoded.exp && decoded.exp < currentTime) {
        this.clearTokens();
        return false;
      }
      
      // Store token with additional security. If storage is blocked, fail
      // closed instead of throwing during render.
      const wroteToken = storageSet(this.tokenKey, token);
      storageSet(this.userIdKey, decoded.sub || '');
      storageSet(this.emailKey, decoded.email || '');
      storageSet(this.loginTimestampKey, Date.now().toString());
      
      return wroteToken;
    } catch (error) {
      console.error('Invalid token format:', error);
      this.clearTokens();
      return false;
    }
  }

  // Get token with validation
  getToken() {
    const token = storageGet(this.tokenKey);
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      if (decoded.exp && decoded.exp < currentTime) {
        this.clearTokens();
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('Invalid token format:', error);
      this.clearTokens();
      return null;
    }
  }

  // Check if token is valid
  isTokenValid() {
    const token = this.getToken();
    return token !== null;
  }

  // Get token expiration time
  getTokenExpiration() {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.exp ? new Date(decoded.exp * 1000) : null;
    } catch (error) {
      return null;
    }
  }

  // Check if token will expire soon (within 5 minutes)
  isTokenExpiringSoon() {
    const expiration = this.getTokenExpiration();
    if (!expiration) return true;
    
    const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
    return expiration < fiveMinutesFromNow;
  }

  // Clear all tokens and user data
  clearTokens() {
    storageRemove(this.tokenKey);
    storageRemove(this.userIdKey);
    storageRemove(this.emailKey);
    storageRemove(this.loginTimestampKey);
    storageRemove(this.currentUserKey);
  }

  // Get user ID from token
  getUserId() {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.sub || null;
    } catch (error) {
      return null;
    }
  }

  // Get user email from token
  getUserEmail() {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      return decoded.email || null;
    } catch (error) {
      return null;
    }
  }

  // Set current user data
  setCurrentUser(userData) {
    if (userData) {
      storageSet(this.currentUserKey, JSON.stringify(userData));
    }
  }

  // Get current user data
  getCurrentUser() {
    try {
      const userData = storageGet(this.currentUserKey);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

/** Set before a forced logout so the login page does not bounce back into the app. */
export const AUTH_FAILED_FLAG = "ne_auth_failed";

export default tokenManager;

